import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { fetchGameById } from '@/lib/api';
import { FormattedMessage } from 'react-intl';
import Rtp from '@/components/Rtp/Rtp';
import IconButton from '@mui/material/IconButton';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import useHasUserPermissions from '@/hooks/useHasUserPermissions';
import SelectThumbnailModal from './SelectThumbnailModal';

const GameHeader = ({ gameId }: { gameId: string | null }) => {
  const [thumbnailModalOpen, setThumbnailModalOpen] = useState(false);
  const canUpdateThumbnail = useHasUserPermissions([
    'marketing_assets.update',
    'marketing_assets.create',
  ]);
  const { data: game } = useQuery({
    queryKey: ['games', gameId],
    queryFn: () => fetchGameById(Number(gameId || 0)),
  });

  return (
    <Root>
      <LogoWrapper>
        <GameLogo url={game?.photo_url || ''} />
        {canUpdateThumbnail && (
          <ThumbnailGearButton
            onClick={() => setThumbnailModalOpen(true)}
            aria-label="Select Thumbnail"
          >
            <SettingsOutlinedIcon fontSize="inherit" />
          </ThumbnailGearButton>
        )}
      </LogoWrapper>
      {game && (
        <SelectThumbnailModal
          game={game}
          open={thumbnailModalOpen}
          onClose={() => setThumbnailModalOpen(false)}
        />
      )}
      <GameDetails isBig>
        <Label>
          <FormattedMessage
            id="partnerName"
            defaultMessage="Game Name"
          />
        </Label>
        <Value>{game?.game_name}</Value>
      </GameDetails>
      <Divider />
      <GameDetails>
        <Label>
          <FormattedMessage id="status" defaultMessage="Status" />
        </Label>
        <Value>{game?.status}</Value>
      </GameDetails>
      <Divider />
      <GameDetails>
        <Label>
          <FormattedMessage
            id="releaseDate"
            defaultMessage="Release Date"
          />
        </Label>
        <Value>{game?.release_date}</Value>
      </GameDetails>
      <Divider />
      <GameDetails>
        <Label>
          <FormattedMessage id="rtp" defaultMessage="RTP" />
        </Label>
        <Value>
          <Rtp value={game?.rtp} />
        </Value>
      </GameDetails>
    </Root>
  );
};

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  padding: `${theme.spacing(2)} 40px`,
  gap: theme.spacing(2),
}));

const LogoWrapper = styled('div')({
  position: 'relative',
  flexShrink: 0,
});

const GameLogo = styled('div')<{ url: string }>(({ theme, url }) => ({
  width: 80,
  height: 80,
  background: theme.palette.grey50,
  border: `1px solid ${theme.palette.grey200}`,
  borderRadius: theme.radii.xxs,
  backgroundImage: url ? `url(${url})` : undefined,
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
}));

const ThumbnailGearButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: -8,
  right: -8,
  width: 24,
  height: 24,
  padding: 4,
  fontSize: 14,
  color: theme.palette.iconBlack,
  background: theme.palette.white,
  border: `1px solid ${theme.palette.grey200}`,
  boxShadow: '0px 2px 4px rgba(19, 25, 39, 0.15)',

  '&:hover': {
    background: theme.palette.grey200,
  },
}));

const GameDetails = styled('div')<{ isBig?: boolean }>(
  ({ isBig }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 8,
    flexGrow: isBig ? 1 : 0,
    flexShrink: isBig ? 0 : 1,
    flexBasis: '50%',
  }),
);

const Label = styled('div')(({ theme }) => ({
  fontSize: 12,
  lineHeight: '16px',
  color: theme.palette.textSecondaryDarkGrey,
}));

const Value = styled('div')({
  fontSize: 14,
  lineHeight: '20px',
});

const Divider = styled('div')(({ theme }) => ({
  width: 1,
  height: '50px',
  background: theme.palette.grey200,
}));

export default GameHeader;
